import React, { useState } from 'react';
import { 
  Search, Filter, Download, ChevronLeft, ChevronRight, 
  Calendar, TrendingUp, TrendingDown, DollarSign, 
  ShoppingBag, Users, Package, Star, Clock, Eye,
  BarChart3, PieChartIcon, LineChartIcon, FileText, Printer,
  Mail, Share2, Zap, Award, Crown, Rocket
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart as ReLineChart, Line
} from 'recharts';
import { Link } from 'react-router-dom';

const Reports = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [reportType, setReportType] = useState('overview');
  const [dateRange, setDateRange] = useState('month');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Chart Data
  const revenueData = [
    { month: 'Jan', revenue: 125000, orders: 45, profit: 87500 },
    { month: 'Feb', revenue: 145000, orders: 62, profit: 101500 },
    { month: 'Mar', revenue: 168000, orders: 78, profit: 117600 },
    { month: 'Apr', revenue: 210000, orders: 92, profit: 147000 },
    { month: 'May', revenue: 245000, orders: 108, profit: 171500 },
    { month: 'Jun', revenue: 230000, orders: 95, profit: 161000 },
    { month: 'Jul', revenue: 267000, orders: 112, profit: 186900 },
    { month: 'Aug', revenue: 289000, orders: 125, profit: 202300 },
    { month: 'Sep', revenue: 278000, orders: 118, profit: 194600 },
    { month: 'Oct', revenue: 312000, orders: 135, profit: 218400 },
    { month: 'Nov', revenue: 345000, orders: 148, profit: 241500 },
    { month: 'Dec', revenue: 420000, orders: 165, profit: 294000 },
  ];

  const categoryData = [
    { name: 'Indoor Plants', value: 35, color: '#10b981' },
    { name: 'Outdoor Plants', value: 25, color: '#3b82f6' },
    { name: 'Flowering Plants', value: 20, color: '#f59e0b' },
    { name: 'Air Purifying', value: 12, color: '#8b5cf6' },
    { name: 'Others', value: 8, color: '#ef4444' },
  ];

  const topProducts = [
    { id: 1, name: 'Monstera Deliciosa', sales: 1245, revenue: 560250, growth: '+15%' },
    { id: 2, name: 'Snake Plant', sales: 987, revenue: 295000, growth: '+8%' },
    { id: 3, name: 'Fiddle Leaf Fig', sales: 876, revenue: 744600, growth: '+22%' },
    { id: 4, name: 'Peace Lily', sales: 765, revenue: 267750, growth: '+5%' },
    { id: 5, name: 'Aloe Vera', sales: 654, revenue: 98100, growth: '-2%' },
  ];

  const recentOrders = [
    { id: 'ORD-7829', customer: 'Rahul Sharma', amount: 2450, status: 'Delivered', date: 'Oct 24, 2024' },
    { id: 'ORD-7830', customer: 'Priya Patel', amount: 1200, status: 'Delivered', date: 'Oct 25, 2024' },
    { id: 'ORD-7831', customer: 'Amit Singh', amount: 4890, status: 'Shipped', date: 'Oct 25, 2024' },
    { id: 'ORD-7832', customer: 'Sneha Gupta', amount: 850, status: 'Processing', date: 'Oct 26, 2024' },
    { id: 'ORD-7833', customer: 'Vikram Verma', amount: 3100, status: 'Delivered', date: 'Oct 26, 2024' },
  ];

  const stats = {
    totalRevenue: 3245000,
    totalOrders: 1342,
    totalCustomers: 8450,
    avgOrderValue: 2418,
    growthRate: 18.5,
    conversionRate: 3.2,
    profitMargin: 32.5,
    returningCustomers: 3420
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor, trend, trendValue }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{typeof value === 'number' ? `₹${value.toLocaleString()}` : value}</h3>
        <span>{title}</span>
        {trend && (
          <div className={`stat-trend ${trend === 'up' ? 'positive' : 'negative'}`}>
            {trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {trendValue} from last month
          </div>
        )}
      </div>
    </div>
  );

  const reportTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'sales', label: 'Sales Report', icon: TrendingUp },
    { id: 'products', label: 'Product Performance', icon: Package },
    { id: 'customers', label: 'Customer Insights', icon: Users },
    { id: 'inventory', label: 'Inventory Report', icon: ShoppingBag },
  ];

  return (
    <div className="admin-reports-modern">
      {/* Header */}
      <div className="reports-header">
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">Comprehensive insights into your business performance</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export Report
          </button>
          <button className="btn-primary">
            <Mail size={16} />
            Schedule Report
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="date-range-bar">
        <div className="date-range-buttons">
          <button className={`range-btn ${dateRange === 'today' ? 'active' : ''}`} onClick={() => setDateRange('today')}>Today</button>
          <button className={`range-btn ${dateRange === 'week' ? 'active' : ''}`} onClick={() => setDateRange('week')}>This Week</button>
          <button className={`range-btn ${dateRange === 'month' ? 'active' : ''}`} onClick={() => setDateRange('month')}>This Month</button>
          <button className={`range-btn ${dateRange === 'quarter' ? 'active' : ''}`} onClick={() => setDateRange('quarter')}>This Quarter</button>
          <button className={`range-btn ${dateRange === 'year' ? 'active' : ''}`} onClick={() => setDateRange('year')}>This Year</button>
        </div>
        <div className="custom-date">
          <Calendar size={16} />
          <input type="date" />
          <span>to</span>
          <input type="date" />
          <button className="apply-btn">Apply</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Revenue" value={stats.totalRevenue} icon={DollarSign} color="#10b981" bgColor="#d1fae5" trend="up" trendValue="12.5%" />
        <StatCard title="Total Orders" value={stats.totalOrders} icon={ShoppingBag} color="#3b82f6" bgColor="#dbeafe" trend="up" trendValue="8.2%" />
        <StatCard title="Total Customers" value={stats.totalCustomers} icon={Users} color="#8b5cf6" bgColor="#e9d5ff" trend="up" trendValue="5.1%" />
        <StatCard title="Average Order Value" value={stats.avgOrderValue} icon={TrendingUp} color="#f59e0b" bgColor="#fed7aa" trend="up" trendValue="3.8%" />
      </div>

      {/* Report Tabs */}
      <div className="report-tabs">
        {reportTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-btn ${reportType === tab.id ? 'active' : ''}`}
              onClick={() => setReportType(tab.id)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {reportType === 'overview' && (
        <>
          {/* Revenue Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <div>
                <h3 className="chart-title">Revenue Overview</h3>
                <p className="chart-subtitle">Monthly revenue and order trends</p>
              </div>
              <div className="chart-legend">
                <span><span className="legend-dot revenue"></span>Revenue</span>
                <span><span className="legend-dot orders"></span>Orders</span>
              </div>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
                  <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                  />
                  <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                  <Area yAxisId="right" type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorOrders)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category Distribution & Top Products */}
          <div className="two-column-grid">
            <div className="chart-card">
              <div className="chart-header">
                <h3 className="chart-title">Sales by Category</h3>
                <p className="chart-subtitle">Product category distribution</p>
              </div>
              <div className="pie-chart-container">
                <ResponsiveContainer width="100%" height="250px">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="category-list">
                {categoryData.map((cat, idx) => (
                  <div key={idx} className="category-item">
                    <span className="category-color" style={{ backgroundColor: cat.color }}></span>
                    <span className="category-name">{cat.name}</span>
                    <span className="category-value">{cat.value}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-header">
                <h3 className="chart-title">Top Performing Products</h3>
                <p className="chart-subtitle">Best sellers by revenue</p>
              </div>
              <div className="top-products-list">
                {topProducts.map((product, idx) => (
                  <div key={product.id} className="top-product-item">
                    <div className="product-rank">#{idx + 1}</div>
                    <div className="product-info">
                      <div className="product-name">{product.name}</div>
                      <div className="product-stats">
                        <span>📦 {product.sales} sold</span>
                        <span>💰 ₹{product.revenue.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="product-growth" style={{ color: product.growth.startsWith('+') ? '#10b981' : '#ef4444' }}>
                      {product.growth}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Orders Table */}
          <div className="table-card">
            <div className="table-header">
              <h3 className="chart-title">Recent Orders</h3>
              <Link to="/admin/orders" className="view-all-link">View All Orders →</Link>
            </div>
            <div className="table-responsive">
              <table className="recent-orders-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="order-id">{order.id}</td>
                      <td className="customer-name">{order.customer}</td>
                      <td className="order-date">{order.date}</td>
                      <td className="order-amount">₹{order.amount.toLocaleString()}</td>
                      <td className="order-status">
                        <span className={`status-badge ${order.status.toLowerCase()}`}>
                          {order.status}
                        </span>
                       </td>
                     </tr>
                   ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Sales Report Tab */}
      {reportType === 'sales' && (
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Sales Performance</h3>
            <p className="chart-subtitle">Monthly sales trends and projections</p>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="400px">
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8ecef" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} tickFormatter={(value) => `₹${value/1000}k`} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px' }}
                  formatter={(value) => [`₹${value.toLocaleString()}`, 'Revenue']}
                />
                <Bar dataKey="revenue" fill="#10b981" radius={[8, 8, 0, 0]} />
                <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={3} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default Reports;